import { Environment } from '../../env/Environment';
import {
  getAccessToken,
  getRefreshToken,
  storeTokens,
} from './session-storage';
import { Cornerstone } from '@rpi/api';
import type { AxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

// Official license to let granted as follows:
// Tyler Schloesser
// 3:50 PM
// yes I grant you one module-scope let XD
let refreshIntoAccessTokenPromise: Promise<string | undefined> | undefined;

const tokenValid = (token: string | undefined | null) => {
  const decoded = token ? jwtDecode(token) : undefined;

  return (
    decoded?.exp &&
    // if the token is about to expire, we consider it invalid
    decoded?.exp - Environment.API.JWT_EARLY_TERMINATION_PERIOD >
      Date.now() / 1000
  );
};

const configWithToken = (
  config: AxiosRequestConfig,
  token: string | undefined | null,
) => {
  if (tokenValid(token)) {
    const modifiedConfig = { ...config };

    modifiedConfig.headers = {
      ...modifiedConfig.headers,
      Authorization: `Bearer ${token}`,
    };

    return modifiedConfig;
  }

  return undefined;
};

// sets and returns refreshIntoAccessTokenPromise so that it makes a refresh call and then returns the access token
const createRefreshPromise = () => {
  refreshIntoAccessTokenPromise = (async (): Promise<string | undefined> => {
    try {
      const response = await Cornerstone.refreshToken({});

      const accessToken = response.data.accessToken;
      const refreshToken = response.data.refreshToken;

      if (accessToken && refreshToken) {
        storeTokens(accessToken, refreshToken);
      }

      // we don't want this refresh promise to be reused again after this
      refreshIntoAccessTokenPromise = undefined;

      return accessToken;
    } catch (error) {
      // if the refresh fails, we need to treat the resulting access token as undefined
      return undefined;
    }
  })();

  return refreshIntoAccessTokenPromise;
};

const refreshThenConfig = (config: AxiosRequestConfig) => {
  // if the access token wasn't valid, we need to refresh, we might already have a refresh promise though
  if (refreshIntoAccessTokenPromise === undefined) {
    // it's time to refresh the tokens
    if (tokenValid(getRefreshToken())) {
      return (async () =>
        configWithToken(config, await createRefreshPromise()) || config)();
    }

    // invalid refresh token, so we fail
    return undefined;
  } else {
    // already have an outgoing refresh promise, wait for it
    return (async () =>
      configWithToken(config, await refreshIntoAccessTokenPromise) || config)();
  }
};

const refreshConfigForRefreshOrLogoutEndpoint = (
  config: AxiosRequestConfig,
) => {
  // if we're hitting the refresh or logout endpoints then we return a config with the refresh token
  // hard coded string is kind of ugly but I'm not sure how to get it from orval (it's not in the current interface)
  if (config.url?.endsWith('/auth/refresh-token')) {
    return configWithToken(config, getRefreshToken()) || config;
  } else if (config.url?.endsWith('/auth/logout')) {
    return configWithToken(config, getRefreshToken()) || config;
  }

  return undefined;
};

export const authInterceptor = (config: AxiosRequestConfig) => {
  // only authenticate blurb-core requests
  if (config.baseURL === Environment.API.URL.BLURBCORE) {
    return (
      refreshConfigForRefreshOrLogoutEndpoint(config) ||
      configWithToken(config, getAccessToken()) ||
      refreshThenConfig(config) ||
      config
    );
  }

  // everything else failed, just return the config as is
  return config;
};
